// reactjs imports
import { useEffect, useRef, useState } from "react";

// firebase imports
import { getAuth } from "firebase/auth";
import { app } from "../firebase/firebase-init";

// component imports
import Loading from "../component/Loading";
import Header from "../component/Header";

const CourseStudentCount = ({ courseName }) => {
  const [count, setCount] = useState(null);

  useEffect(() => {
    const getDocsCount = async () => {
      try {
        const { query, where, collection, getCount } = await import(
          "firebase/firestore/lite"
        );
        const { db } = await import("../firebase/firebase-init");

        const q = query(
          collection(db, "applications"),
          where("course", "==", courseName)
        );

        const snapshot = await getCount(q);

        const count = snapshot.data().count;

        setCount(String(count));
      } catch (error) {
        console.log(error);
        alert("Something went wrong. Please reload the page");
      }
    };

    getDocsCount();
  }, [courseName]);

  return (
    <>
      <li className="list-group-item d-flex justify-content-between align-items-center">
        <span className="text-uppercase">{courseName}</span>

        {count ? (
          <span className="badge bg-primary rounded-pill">{count}</span>
        ) : (
          <span className="badge bg-primary rounded-pill placeholder-glow p-0">
            <span className="placeholder py-2 px-3"></span>
          </span>
        )}
      </li>
    </>
  );
};

const StudentRow = ({ iterableData, currentlyWorking, docID, photo }) => {
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [defaultValue, setDefaultValue] = useState(currentlyWorking);

  const inputRef = useRef();

  async function handleSubmit() {
    if (updating) return;

    const { value } = inputRef.current;

    if (value.trim() === "")
      return alert("Please enter where this student is currently working");

    if (value.trim() === defaultValue?.trim())
      return alert("Please enter a different value");

    try {
      setUpdating(true);

      const { doc, updateDoc } = await import("firebase/firestore/lite");
      const { db } = await import("../firebase/firebase-init");

      const docRef = doc(db, "applications", docID);

      await updateDoc(
        docRef,
        {
          currentlyWorking: value.trim(),
        },
        { merge: true }
      );

      setDefaultValue(value.trim());

      alert("Successfully updated");
    } catch (error) {
      console.log(error);
      alert("Something went wrong. Please try again later");
    } finally {
      setUpdating(false);
    }
  }

  async function handleDelete() {
    const confirm = window.confirm("Are you sure you want to delete this application?");

    if(!confirm) return;

    try {
      setDeleting(true);

      const {doc, deleteDoc} = await import("firebase/firestore/lite");
      const {db} = await import("../firebase/firebase-init");  
      
      await deleteDoc(doc(db, "applications", docID));

      if(photo) {
        const { getStorage, ref, deleteObject } = await import("firebase/storage");

        const storage = getStorage();

        const regex = /\/([^\/?]+)\?/;
        const match = photo.match(regex);
        const filename = match[1];

        // Create a reference to the file to delete
        const fileRef = ref(storage, filename);

        await deleteObject(fileRef);
      }

      alert(`Successfully Deleted`);

      window.location.reload();
    } catch (error) {
      console.log(error);
      alert("Something went wrong. Please try again later");
    } finally {
      setDeleting(false);
    }
  }

  const allData = [
    ...iterableData,
    <input
      ref={inputRef}
      type="text"
      className="form-control rounded-1 border border-secondary-subtle text-capitalize w-100 px-2 py-1"
      placeholder="Currently working at"
      defaultValue={defaultValue}
    />,
    <button
      onClick={handleSubmit}
      disabled={updating}
      className="btn btn-primary btn-sm"
      type="button">
      {updating && (
        <div
          className="spinner-border spinner-border-sm me-2"
          role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      )}
      Submit
    </button>,
    <button
    onClick={handleDelete}
    disabled={deleting}
    className="btn btn-danger btn-sm"
    type="button">
    {deleting && (
      <div
        className="spinner-border spinner-border-sm me-2"
        role="status">
        <span className="visually-hidden">Deleting...</span>
      </div>
    )}
    Delete
  </button>,
  ];

  return (
    <tr className="text-center text-capitalize">
      {allData.map((data, index) => (
        <td
          className="text-truncate"
          key={index}>
          {data}
        </td>
      ))}
    </tr>
  );
};

const Content = () => {
  const [applications, setApplications] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [downloading, setDownloading] = useState(false);

  const filteredStudents = applications?.filter((student) => {
    return (
      student?.fullName?.toLowerCase()?.includes(searchQuery.toLowerCase()) ||
      student?.id?.toString()?.includes(searchQuery) ||
      student?.course?.toLowerCase()?.includes(searchQuery.toLowerCase()) ||
      student?.district?.toLowerCase()?.includes(searchQuery.toLowerCase()) ||
      student?.contact?.includes(searchQuery) ||
      student?.applicationID?.toLowerCase()?.includes(searchQuery.toLowerCase())
    );
  });

  const tableHeaderCells = [
    "full name",
    "course",
    "district",
    "application ID",
    "contact",
    "working at",
    "",
    ""
  ];
  const coursesList = ["gdm", "cdm", "icap", "dict"];

  const columns = [
    "fullName",
    "contact",
    "district",
    "course",
    "batch",
    "currentlyWorking",
    "applicationID",
    "fatherName",
    "motherName",
    "dateOfBirth",
    "gender",
    "email",
    "lastEducationQualification",
    "occupation",
    "organization",
    "designation",
    "workingSector",
    "presentAddress",
    "permanentAddress",
    "guardianContact",
  ];

  async function handleDownload() {
    if (downloading) return;

    try {
      setDownloading(true);

      const rearrangedData = filteredStudents.map((item, index) => {
        const rearrangedItem = {};

        rearrangedItem['Serial'] = index + 1;
        
        columns.forEach((column) => {
          rearrangedItem[column] = item[column];
        });
        return rearrangedItem;
      });

      const {
        utils: { json_to_sheet, book_new, book_append_sheet },
        write,
      } = await import("xlsx");

      const worksheet = json_to_sheet(rearrangedData);
      const workbook = book_new();
      book_append_sheet(workbook, worksheet, "Sheet1");

      const excelBuffer = write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const excelData = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });
      const excelFile = URL.createObjectURL(excelData);

      const link = document.createElement("a");
      link.href = excelFile;
      link.download = "applications.xlsx";
      link.click();

      link.remove();
    } catch (error) {
      console.error("Failed to import the XLSX module:", error);
    } finally {
      setDownloading(false);
    }
  }

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const { query, collection, getDocs } = await import(
          "firebase/firestore/lite"
        );
        const { db } = await import("../firebase/firebase-init");

        const q = query(collection(db, "applications"));

        const querySnapshot = await getDocs(q);

        const fetchedData = [];

        querySnapshot.forEach((doc) => {
          fetchedData.push({ ...doc.data(), docID: doc.id });
        });

        setApplications(fetchedData);
      } catch (error) {
        setApplications(undefined);
      }
    };

    fetchStudents();
  }, []);

  return (
    <>
      <Header admin />

      <div className="row gy-3 m-3">
        <div className="col-md-3">
          <ul className="list-group">
            {coursesList.map((course, index) => (
              <CourseStudentCount
                key={index}
                courseName={course}
              />
            ))}
          </ul>
        </div>

        <div className="col-md-9">
          <input
            type="text"
            className="form-control mb-3"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by Name, Course, ID, District, Contact or Application ID"
          />

          <div className="border rounded-2 overflow-hidden">
            <h5 className="bg-primary bg-gradient text-white px-3 py-2 mb-0">
              Students List
            </h5>

            <div className="overflow-auto">
              {applications === null ? (
                <>
                  <h5 className="px-2 py-1">Loading...</h5>
                </>
              ) : applications === undefined ? (
                <>
                  <h5 className="px-2 py-1">
                    Something went wrong. Please reload the page
                  </h5>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    disabled={downloading}
                    onClick={handleDownload}
                    className="btn btn-primary position-fixed bottom-0 end-0 m-3 p-3 d-flex align-items-center justify-content-center text-center rounded-circle">
                    {downloading ? (
                      <div
                        className="spinner-border spinner-border-sm"
                        role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        fill="currentColor"
                        className="bi bi-arrow-down-circle"
                        viewBox="0 0 16 16">
                        <path
                          fillRule="evenodd"
                          d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8zm15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM8.5 4.5a.5.5 0 0 0-1 0v5.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V4.5z"
                        />
                      </svg>
                    )}

                    <span className="visually-hidden">download excel</span>
                  </button>

                  <table className="table table-bordered table-hover my-0">
                    <thead>
                      <tr className="text-center text-capitalize">
                        <th scope="col">No.</th>

                        {tableHeaderCells.map((cell, index) => (
                          <th
                            className="text-truncate"
                            key={index}
                            scope="col">
                            {cell}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {filteredStudents.map((application, index) => {
                        const {
                          fullName,
                          courseType,
                          course,
                          district,
                          contact,
                          applicationID,
                          docID,
                          currentlyWorking,
                          photo
                        } = application;

                        const iterableData = [
                          index + 1,
                          <a
                            target="_blank"
                            rel="noreferrer"
                            href={`/student?ID=${docID}`}>
                            {fullName}
                          </a>,
                          `${course.toUpperCase()} (${courseType})`,
                          district,
                          applicationID,
                          contact,
                        ];

                        return (
                          <StudentRow
                            key={index}
                            currentlyWorking={currentlyWorking}
                            iterableData={iterableData}
                            docID={docID}
                            photo={photo}
                          />
                        );
                      })}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const Admin = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const auth = getAuth(app);

    auth.onAuthStateChanged((user) => {
      setIsLoggedIn(user);

      if (!user) {
        window.location.href = "/login";
      }
    });
  }, []);

  return (
    <>
      {isLoggedIn === false ? (
        <Loading />
      ) : isLoggedIn ? (
        <Content />
      ) : (
        `Logged Out. Redirecting...`
      )}
    </>
  );
};

export default Admin;
